import {
  ShieldCheck,
  FileSpreadsheet, 
  Network,
  Shield, 
  BarChart3,
  Video,
  Code2,
  Server,
  Box,
  FileText
} from "lucide-react";

export const projects = [
    {
        title: "Bank Infrastructure Design Playbook",
        subtitle: "Secure, Resilient & Compliant Reference Architecture",
        desc: "An 11-zone reference architecture for a modern banking environment, covering perimeter to physical security, SWIFT isolation, 200-branch networking, and dual-site DR — aligned to ISO 27001, NIST CSF 2.0, Bangladesh Bank ICT, and SWIFT CSP.",
        objective:
            "Design a zone-based, identity-driven, compliance-aligned banking infrastructure that eliminates flat network paths, isolates critical systems like SWIFT and CBS, and provides a realistic 3-phase implementation roadmap from baseline controls to SOAR and ZTNA maturity.",
        type: "Architecture Design",
        risk: "High",
        coverage: "Enterprise Banking",
        status: "Operational",
        tags: ["FortiGate", "F5 BIG-IP", "Wazuh", "HIVE", "VMware", "ISO 27001", "NIST CSF", "SWIFT CSP"],
        accent: "teal",
        icon: ShieldCheck, 
        images: ["/assets/images/Case Studies/Cyber Architecture/Cyber Architecture.png",],
        summary:
            "A full reference architecture for a secure, resilient banking environment. Defines 11 security zones, maps a 15+ tool security stack, documents traffic flows, and aligns every control to ISO 27001, NIST CSF 2.0, Bangladesh Bank ICT guidelines, and SWIFT CSP.",
        pipeline: [
            {
                title: "Zone segmentation design",
                desc: "Defined 11 logical zones — Internet Edge, Perimeter, DMZ, Application, Database, SWIFT, Security Ops, HQ Users, Branch Network, Physical Security, and Voice — with default-deny routing between all zones.",
            },
            {
                title: "Security stack mapping",
                desc: "Mapped FortiGate 1100E HA, F5 BIG-IP WAF, FortiMail 400F, Wazuh SIEM, FortiAnalyzer, Elasticsearch, HIVE IR, and Kaspersky into a coordinated detection-and-response stack.",
            },
            {
                title: "Compliance alignment",
                desc: "Documented how each zone and control satisfies ISO 27001 Annex A, NIST CSF 2.0 functions, Bangladesh Bank ICT requirements, and SWIFT CSP mandatory controls.",
            },
            {
                title: "Branch & DR architecture",
                desc: "Designed 200-branch network using FortiGate 600E with IPSec/MPLS tunnels, no branch-to-branch routing, and a warm-standby DR site with VMware replication and RPO < 15 minutes.",
            },
            {
                title: "Roadmap & playbook",
                desc: "Produced a 3-phase implementation roadmap — from core segmentation and logging (Phase 1) through PAM and MFA hardening (Phase 2) to SOAR, ZTNA, and DLP maturity (Phase 3).",
            },
        ],
        highlights: [
            "11-zone segmented architecture with no flat network paths between any zones",
            "SWIFT zone fully isolated with dedicated firewall and two-person operator control",
            "Dual-site DR design with VMware replication — RPO < 15 min, RTO < 1 hour",
            "Aligned to ISO 27001, NIST CSF 2.0, Bangladesh Bank ICT, and SWIFT CSP",
            "200-branch IPSec/MPLS network with centralised policy enforcement",
            "3-phase implementation roadmap from baseline controls to SOAR and ZTNA maturity",
        ],
        visualization: [
            "Full zone architecture diagram (11 zones)",
            "Traffic flow matrix across zones",
            "Compliance control mapping table",
            "3-phase implementation roadmap",
            "Branch network topology",
            "DR and HA architecture layout",
        ],
        auditUse: [
            "Demonstrates architecture design and strategic security planning capability",
            "Supports ISO 27001 and NIST CSF control mapping conversations with auditors",
            "Shows SWIFT CSP isolation design for banking compliance reviews",
            "Useful as a reference for DR and business continuity documentation",
        ],
        stack: [
            "FortiGate 1100E",
            "F5 BIG-IP WAF",
            "FortiMail 400F",
            "Wazuh SIEM",
            "FortiAnalyzer 400E",
            "Elasticsearch",
            "HIVE IR",
            "Kaspersky",
            "VMware vSphere",
            "ZKTeco",
            "Hikvision",
            "Active Directory",
            "IPSec/MPLS",
        ],
        impact: [
            "Zero flat network paths — every zone is isolated and default-deny",
            "SWIFT environment fully air-gapped from user and management zones",
            "200-branch rollout designed with centralised policy and local resilience",
            "Full compliance coverage across four regulatory frameworks",
        ],
        relatedProjects: [
            "This architecture directly informs the monitoring design used in the FortiAnalyzer Monitoring & Log Correlation case study.",
            "The branch network zone (Z9) aligns with the endpoint hardening and inventory work in the PC Inventory & Risk Analysis Platform.",
        ],
        customComponent: "CyberArchitecture",
    },

    {
        title: "PC Inventory & Risk Analysis Platform",
        subtitle: "Asset Discovery, Endpoint Inspection, and Audit Reporting",
        desc: "A two-stage PowerShell and React platform that discovers enterprise endpoints, enriches asset data, scores risk, maps findings to remediation guidance, and generates audit-ready reports for branch and enterprise environments.",
        objective:
        "Discover, identify, assess, analyze, score, and guide remediation for enterprise endpoints across branches using a repeatable inventory and risk workflow.",
        type: "Security Platform",
        risk: "High",
        coverage: "Enterprise Networks",
        status: "Operational",
        tags: ["C#", "PowerShell", "React", "MITRE ATT&CK"],
        accent: "cyan",
        icon: Network,
        images: [
            "/assets/images/Projects/PC-Inventory/PC-Inventory-1.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-2.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-3.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-4.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-5.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-6.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-7.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-8.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-9.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-10.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-11.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-12.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-13.png",
            "/assets/images/Projects/PC-Inventory/PC-Inventory-14.png",
        ],
        summary:
        "Built to map entire networks, inspect endpoints deeply, calculate posture-based risk, and export branch-specific or enterprise-wide reports with LAN diagrams.",
        pipeline: [
        {
            title: "Bulk network discovery",
            desc: "A PowerShell WMI scanner uses thread-pool parallel processing across an IP range to collect hostname, OS version, antivirus status, and domain posture for broad asset discovery.",
        },
        {
            title: "Deep branch inspection",
            desc: "A second PowerShell script scans a selected branch in detail and captures a wide endpoint posture dataset covering hardware, security, services, encryption, exposure, and local administration status.",
        },
        {
            title: "Timestamped Excel export",
            desc: "Both scan modes generate structured Excel output with proper file naming and timestamps so results can be imported into the React UI and tracked over time.",
        },
        {
            title: "Branch mapping and visualization",
            desc: "The UI maps hosts to branches for easier identification and visualizes the inventory through charts, progress bars, and detailed device views.",
        },
        {
            title: "Risk scoring and MITRE mapping",
            desc: "The UI risk engine calculates device exposure using patch age, OS level, AV posture, domain status, remote access state, encryption, firmware protections, and other security signals, then aligns findings to MITRE-style reasoning.",
        },
        {
            title: "Audit reporting and remediation",
            desc: "Results are printable for audits, include LAN diagram support, and provide remediation guidance for migration, hardening, and operational cleanup.",
        },
        ],
        discovery: {
        script: "PowerShell WMI bulk discovery script",
        method:
            "Thread-pool parallel processing across an IP range to collect host inventory, OS version, AV status, and domain posture.",
        outputs: [
            "Hostname and IP discovery",
            "OS version and old OS detection",
            "Domain migration candidates",
            "AV installation gap detection",
        ],
        },
        deepScan: {
        script: "Branch-level PowerShell deep inspection script",
        columns: [
            "IP","Hostname","DomainStatus","OSVersion","OSBuild","Architecture","LastBootTime","CPU","TotalRAM_GB","RAM_Modules","RAM_ModuleCount",
            "Disk_Types","Disk_Details","Disk_C_GB","Disk_C_Free_GB","HotfixCount","LatestHotfix","PatchAge_Days","AV_Details","LoggedOnUser",
            "LoggedOnUser_PasswordAge_Days","LocalAdmins","Manufacturer","Model","BitLocker_Status","TPM_Present","TPM_SpecVersion","SecureBoot",
            "Firewall_DomainEnabled","Firewall_PrivateEnabled","Firewall_PublicEnabled","SMB1_Enabled","RDP_Enabled","RDP_NLA","RemoteDiag",
            "OpenPorts","ServicesList","Autoruns","SecurityEvents","Disk_Encryption_Details","FirmwareSecureBootPolicy","ExploitMitigations","ScanType","Timestamp","Status",
        ],
        outputs: [
            "Deep endpoint posture capture",
            "Branch-level security visibility",
            "Detailed audit evidence export",
        ],
        },
        riskEngine: {
        inputs: [
            "Patch age",
            "OS version",
            "AV posture",
            "Domain status",
            "Firewall state",
            "RDP exposure",
            "SMB exposure",
            "BitLocker",
            "TPM",
            "Secure Boot",
            "Open ports",
            "Services",
            "Autoruns",
        ],
        mapping: [
            "Map weak posture to MITRE-style risk reasoning",
            "Prioritize endpoints with outdated OS or missing AV",
            "Flag weak hardening and remote access exposure",
        ],
        remediation: [
            "Migrate non-domain devices",
            "Install or repair endpoint protection",
            "Patch unsupported or outdated systems",
            "Enable BitLocker, TPM, and Secure Boot where possible",
            "Harden firewall, RDP, SMB, and service exposure",
        ],
        },
        visualization: [
            "Branch mapping",
            "Bar charts for migration status",
            "Pie charts for OS and risk distribution",
            "Progress bars for audit readiness",
        ],
        auditUse: [
            "Printable audit reports",
            "Branch-wise inventory evidence",
            "LAN diagram support",
            "Migration planning and remediation tracking",
        ],
        relatedProjects: [
            "Legacy Windows 7 upgrade work used this platform to identify unsupported systems.",
            "Automation and audit workflows were built on top of the same inventory and risk data.",
        ],
        highlights: [
            "Two scan modes: full network discovery and deep endpoint inspection",
            "Risk scoring based on patch status, OS version, antivirus posture, services, memory, hardware, and domain membership",
            "PDF export for branch-level and full-environment audit reporting",
            "LAN diagram support for visual review and documentation",
            "Frontend surfaces OS type, device posture, and branch-level mapping",
        ],
        stack: ["PowerShell", "React", "PDF Export", "Diagram Export", "MITRE Mapping"],
        impact: [
            "Improved asset visibility across distributed environments",
            "Standardized endpoint posture analysis for audit use",
            "Reduced manual inventory and reporting workload",
            "Enabled migration planning, hardening review, and remediation tracking from one workflow",
        ],
        customComponent: "PCInventory",
   },

   {
        title: "SecureVault",
        subtitle: "Local-First Encrypted Password Manager for Windows 11",
        desc: "A full-stack personal password manager built on AES-256-GCM encryption, Windows DPAPI integration, and a Chrome/Edge/Firefox browser extension with native messaging — storing all credentials locally with zero cloud dependency.",
        objective: (
                        <>
                            Design and build a production-grade offline password manager with browser autofill,<br />
                            multi-browser extension support, automated credential capture, and a professional<br />
                            Windows installer — deployable on any Windows 11 machine with minimal setup.
                            <br /><br />
                            <strong>Privacy Policy:</strong> <a href="https://hossainabedy.com/policies/securevault/privacy-policy.html" target="_blank" rel="noopener noreferrer">https://hossainabedy.com</a><br />
                            <strong>Support Page:</strong> <a href="https://hossainabedy.com/policies/securevault/support-policy.html" target="_blank" rel="noopener noreferrer">https://hossainabedy.com</a><br />
                            <strong>Extension:</strong> <a href="https://chromewebstore.google.com/detail/securevault/becngobgachhbpglojolpgioljnfddik" target="_blank" rel="noopener noreferrer">https://google.com</a>
                        </>
                    ),
        type: "Security Tool",
        risk: "High",
        coverage: "Windows 11 Desktop",
        status: "Operational",
        tags: ["Python", "PyQt6", "AES-256-GCM", "Chrome Extension", "DPAPI"],
        accent: "red",
        icon: Shield,
        images: [
            "/assets/images/Projects/SecureVault/SecureVault-1.png",
            "/assets/images/Projects/SecureVault/SecureVault-2.png",
            "/assets/images/Projects/SecureVault/SecureVault-3.png",
            "/assets/images/Projects/SecureVault/SecureVault-4.png",
            "/assets/images/Projects/SecureVault/SecureVault-5.png",
            "/assets/images/Projects/SecureVault/SecureVault-6.png",
            "/assets/images/Projects/SecureVault/SecureVault-7.png",
            "/assets/images/Projects/SecureVault/SecureVault-8.png",
            "/assets/images/Projects/SecureVault/SecureVault-9.png",
            "/assets/images/Projects/SecureVault/SecureVault-10.png",
            "/assets/images/Projects/SecureVault/SecureVault-11.png",
        ],
        summary:
        "Built to securely store, autofill, import, and manage passwords entirely offline using military-grade encryption, Windows account binding, and a cross-browser extension with save/update detection.",
        pipeline: [
            {
                title: "Vault creation and authentication",
                desc: "On first launch the user registers a vault with a master password. The vault key is wrapped using Argon2id KDF and stored encrypted. Optionally the key is also wrapped with Windows DPAPI, enabling silent auto-unlock for the registered Windows account on every subsequent launch.",
            },
            {
                title: "AES-256-GCM encrypted storage",
                desc: "Every credential is individually encrypted with AES-256-GCM before being written to a local SQLite database. The vault key never touches disk in plaintext — it lives only in a zeroed Python bytearray in memory, cleared immediately on lock.",
            },
            {
                title: "Browser extension autofill",
                desc: "A Manifest V3 Chrome/Edge extension and a Manifest V2 Firefox extension communicate with the desktop app via Chrome Native Messaging. On any login page a floating button appears for one-click autofill from the local vault.",
            },
            {
                title: "Credential capture and update detection",
                desc: "The content script monitors form submissions using sessionStorage to survive page redirects. On the next page load it checks the vault — showing a Save banner for new credentials or an Update banner when a changed password is detected.",
            },
            {
                title: "Browser password import",
                desc: "A tabbed import wizard decrypts and imports saved passwords directly from Chrome, Edge, and Firefox using DPAPI key extraction and AES-GCM decryption of the browser Login Data SQLite file. Generic CSV import supports Bitwarden, KeePass, and 1Password exports.",
            },
            {
                title: "Windows installer packaging",
                desc: "PyInstaller bundles the app and native host into standalone executables. Inno Setup wraps everything into SecureVaultSetup.exe, registering native host manifests for Chrome, Edge, and Firefox, creating shortcuts, and installing the Firefox extension via enterprise policy automatically.",
            },
        ],
        discovery: {
            script: "Windows DPAPI + Argon2id authentication engine",
            method:
                "Detects the current Windows SID at launch, looks up the linked vault, and decrypts the vault key silently via DPAPI — no password prompt for the registered owner.",
            outputs: [
                "Silent auto-unlock for Windows account owner",
                "Master password fallback for other users",
                "Per-user isolated vault support",
                "In-memory key with zero-on-lock protection",
            ],
        },
        deepScan: {
            script: "Browser extension native messaging pipeline",
            columns: [
                "PING", "GET_CREDENTIALS", "CHECK_CREDENTIAL",
                "SAVE_CREDENTIAL", "UPDATE_CREDENTIAL", "FILL_CREDENTIALS",
                "allowed_origins", "allowed_extensions", "sessionStorage",
                "beforeunload", "MutationObserver", "simulateInput",
                "WeakSet", "domain", "entry_id", "vault_key",
            ],
            outputs: [
                "Real-time login form detection on any website",
                "Autofill from local vault via native messaging",
                "Automatic save and update prompts on form submission",
            ],
        },
        riskEngine: {
            inputs: [
                "AES-256-GCM encryption",
                "Argon2id key derivation",
                "Windows DPAPI key wrapping",
                "In-memory vault key",
                "Zero-on-lock memory clearing",
                "30-second clipboard auto-clear",
                "Cryptographically secure password generator",
                "No cloud sync or external API calls",
            ],
            mapping: [
                "Vault key never written to disk in plaintext",
                "Each entry encrypted individually with unique nonce",
                "DPAPI binding ties vault key to Windows account",
            ],
            remediation: [
                "Master password recovery path always available",
                "Password generator with configurable strength",
                "Clipboard cleared automatically after 30 seconds",
                "Native host log redacts all password fields",
            ],
        },
        visualization: [
            "Category sidebar filtering",
            "Real-time search across all fields",
            "Detail preview panel without decryption",
            "Password strength meter in generator",
        ],
        auditUse: [
            "Fully offline — no network traffic for credential operations",
            "SQLite database with per-field AES-256-GCM encryption",
            "Native messaging host log with password redaction",
            "Privacy policy published at hossainabedy.com",
        ],
        relatedProjects: [
            "PC Inventory platform used similar PowerShell-based credential handling patterns.",
            "SBAC Bank cybersecurity operations informed the threat model and encryption design.",
        ],
        highlights: [
            "AES-256-GCM per-entry encryption with Argon2id key derivation (64MB, 3 passes)",
            "Windows DPAPI auto-unlock — no password prompt for the registered Windows account",
            "Chrome, Edge, and Firefox extension with save/update banner on form submission",
            "Browser password import from Chrome, Edge, Firefox, and generic CSV",
            "Professional Windows installer (Inno Setup + PyInstaller) with automated browser setup",
        ],
        stack: ["Python", "PyQt6", "SQLite", "AES-256-GCM", "Argon2id", "DPAPI", "Chrome Extension MV3", "Firefox MV2", "PyInstaller", "Inno Setup"],
        impact: [
            "Zero cloud dependency — all credentials stored and encrypted locally on-device",
            "Single installer deploys the full stack including native host and browser extensions",
            "Cross-browser support covering Chrome, Edge, and Firefox from one codebase",
            "Published to Chrome Web Store with permanent extension ID tied to a signing key",
        ],
        customComponent: "SecureVault",
    },

    {
        title: "CIB Automation",

        subtitle: "CIB PDF Extraction, Financial Validation, Review, and Audit Automation",

        desc:
            "An enterprise Python-based banking automation platform that processes batches of CIB PDF reports, extracts financial and reporting information, validates and reconciles extracted values, routes exceptions for review, tracks processing progress, generates audit-ready Word reports, and provides controlled multi-user processing with concurrent PDF workers.",

        objective:
            "Automate the collection, extraction, validation, reconciliation, review, and reporting of CIB data from large PDF reports while reducing repetitive manual processing, improving consistency, preserving audit traceability, and providing a reliable multi-user workflow.",

        type: "Banking Automation Platform",

        risk: "High",

        coverage: "Enterprise CIB Processing",

        status: "Operational",

        tags: [
            "Python",
            "Flask",
            "Jinja",
            "PyMuPDF",
            "SQLite",
            "PDF Extraction",
            "Financial Validation",
            "Concurrency",
            "Audit Logging",
            "Word Reporting"
        ],

        accent: "purple",

        icon: FileText,

        images: [
            "/assets/images/Projects/SBAC-CIB/SBAC-CIB-1.png",
            "/assets/images/Projects/SBAC-CIB/SBAC-CIB-2.png",
            "/assets/images/Projects/SBAC-CIB/SBAC-CIB-3.png",
            "/assets/images/Projects/SBAC-CIB/SBAC-CIB-4.png",
            "/assets/images/Projects/SBAC-CIB/SBAC-CIB-5.png",
            "/assets/images/Projects/SBAC-CIB/SBAC-CIB-6.png",
        ],

        summary:
            "Built to transform large batches of CIB PDF reports into a controlled processing workflow using PyMuPDF-based extraction, financial validation and reconciliation, review routing, concurrent processing, cancellation, SQLite-backed audit tracking, and automated Word report generation.",

        pipeline: [

            {
                title: "Batch PDF ingestion",

                desc:
                    "The platform accepts batches of CIB PDF reports and creates a tracked processing run with source metadata, reporting dates, user information, and batch-level audit details.",
            },

            {
                title: "High-performance PDF extraction",

                desc:
                    "PyMuPDF is used for efficient page-level PDF processing, including large CIB reports containing hundreds or more than one thousand pages while retaining cooperative cancellation and progress tracking.",
            },

            {
                title: "Financial data extraction",

                desc:
                    "Required CIB information is extracted from report content, including financial figures and reporting metadata such as Gross STD through BLW, Stay Order, Last Reporting Date, and CIB Date.",
            },

            {
                title: "Validation and reconciliation",

                desc:
                    "Extracted financial values are validated against the application's business rules and reconciled before a report is classified as successful, requiring review, or failed.",
            },

            {
                title: "Exception and review routing",

                desc:
                    "Reports that contain genuine inconsistencies or values that cannot be safely reconciled are routed to REVIEW rather than being silently accepted as valid output.",
            },

            {
                title: "Fair concurrent processing",

                desc:
                    "A bounded global PDF worker pool allows multiple users and batches to process concurrently while a fair scheduler distributes available processing capacity between active batches without allocating unlimited workers per user.",
            },

            {
                title: "Progress and cancellation",

                desc:
                    "The application exposes batch-level and current-PDF progress, page-level activity, worker utilization, processing stages, elapsed timing, and cooperative STOP handling for long-running PDF operations.",
            },

            {
                title: "Word report generation",

                desc:
                    "After PDF processing completes, the application generates a consolidated Word summary report and exposes the download action only after the report is actually ready.",
            },

            {
                title: "Audit and history",

                desc:
                    "Processing batches, source information, user identity, outcomes, timestamps, errors, and report history are retained through SQLite-backed audit and history records.",
            },

        ],

        processingEngine: {

            method:
                "PyMuPDF-based page-level PDF extraction with business-rule validation, financial reconciliation, cooperative cancellation, bounded concurrent workers, fair batch scheduling, and audit logging.",

            inputs: [

                "CIB PDF reports",

                "Batch source folders",

                "Authenticated employee information",

                "Reporting-date metadata",

            ],

            outputs: [

                "Extracted CIB financial data",

                "SUCCESS / REVIEW / FAILED classification",

                "Processing batch history",

                "Audit records",

                "Word summary report",

                "Archived processing results",

            ],

        },

        concurrencyModel: {

            architecture: [

                "Bounded global PDF worker pool",

                "Fair dynamic batch scheduler",

                "Per-batch pending PDF queues",

                "Dynamic worker distribution across active batches",

                "Cooperative STOP handling",

            ],

            principle:
                "The platform maintains a global processing ceiling rather than allocating a fixed number of workers per user, allowing a single active batch to use available capacity while preventing one user's workload from monopolizing the system when multiple users are processing simultaneously.",

        },

        dataValidation: {

            validation:
                "PDF extraction validation, financial reconciliation, CIB reporting-date validation, extracted-value consistency checks, SUCCESS/REVIEW/FAILED classification, duplicate prevention, and controlled error handling.",

            protections: [

                "Extraction and validation are isolated from multi-user scheduling",

                "Large PDFs are processed with page-level cancellation checks",

                "Temporary database contention is retried rather than immediately failing a successfully processed PDF",

                "SQLite WAL and busy-timeout protection reduce concurrent database contention",

                "Database transactions are retried as complete units to avoid partial saves",

                "Duplicate-safe processing prevents repeated database records",

                "Genuine extraction or validation exceptions remain visibly classified",

            ],

        },

        audit: [

            "Timestamped processing batches",

            "Authenticated user tracking",

            "Source folder and source file tracking",

            "Client IP and user-agent metadata",

            "Application version tracking",

            "Processing duration",

            "Worker-count snapshot",

            "SUCCESS / REVIEW / FAILED outcomes",

            "Processing error logging",

            "SQLite audit records",

            "Word report generation status",

            "Archived batch history",

        ],

        authentication: {

            method:
                "Supports existing CIB users alongside Password Book employee authentication using employee ID and server-side API validation.",

            controls: [

                "Employee ID-based authentication",

                "Password Book credential validation",

                "CIB-side USER / ADMIN role management",

                "Enable / disable CIB access",

                "Automatic provisioning of first-time Password Book users",

                "Password Book passwords are not stored by CIB",

            ],

        },

        reviewWorkflow: [

            "Process CIB PDF",

            "Extract required financial and reporting information",

            "Validate extracted values",

            "Reconcile applicable financial figures",

            "Classify as SUCCESS, REVIEW, or FAILED",

            "Retain processing details for audit",

            "Generate final Word report after processing completion",

        ],

        visualization: [

            "Live batch processing dashboard",

            "Overall PDF progress",

            "Current PDF progress",

            "Current page and page-count activity",

            "Processing stage",

            "Worker utilization",

            "SUCCESS / REVIEW / FAILED statistics",

            "Queue state and waiting time",

            "Batch timing information",

            "Word report readiness",

            "Batch history",

            "Audit information",

            "User management",

        ],

        operationalUse: [

            "CIB report processing",

            "Financial information extraction",

            "CIB validation and reconciliation",

            "Exception and review handling",

            "Batch processing oversight",

            "Audit and historical traceability",

            "Word report preparation",

        ],

        highlights: [

            "Large-scale CIB PDF extraction using PyMuPDF",

            "Supports very large reports containing hundreds or more than one thousand pages",

            "Automated extraction and validation of required CIB financial information",

            "SUCCESS / REVIEW / FAILED outcome classification",

            "Multi-user fair scheduling with a bounded global PDF worker pool",

            "Page-level progress and cooperative PDF cancellation",

            "SQLite WAL and retry-based database reliability protections",

            "Automated Word summary report generation",

            "Audit-ready batch history and processing metadata",

            "Password Book employee authentication with CIB-side role and access management",

            "Controlled human review for non-reconciling or exceptional results",

        ],

        stack: [

            "Python",

            "Flask",

            "Jinja",

            "Waitress",

            "PyMuPDF",

            "pypdf",

            "python-docx",

            "SQLite",

            "Werkzeug",

            "python-dotenv",

            "Concurrent Thread Workers",

            "Fair Batch Scheduling",

            "PDF Processing",

            "Financial Validation",

            "Audit Logging",

        ],

        impact: [

            "Reduced repetitive manual CIB report extraction and review work",

            "Standardized CIB PDF processing and validation",

            "Improved consistency of extracted financial information",

            "Reduced manual reconciliation and classification effort",

            "Improved visibility into long-running PDF processing",

            "Enabled controlled concurrent processing for multiple users",

            "Reduced avoidable database-lock-related processing failures",

            "Created auditable batch and processing history",

            "Automated preparation of consolidated Word reports",

            "Reduced manual credential administration through Password Book integration",

        ],

        relatedProjects: [

            "The platform is designed as an internal banking automation service that can be deployed centrally and accessed by multiple authorized users.",

            "The processing architecture is intentionally bounded and modular so worker capacity can be benchmarked and adjusted without changing the extraction engine.",

            "Future hardening can extend startup recovery, operational monitoring, and additional resilience controls without replacing the core Flask and SQLite architecture.",

        ],

        customComponent: "SBACCIBAutomation",
       

    },

    {
        title: "RIT Automation",

        subtitle: "Regulatory Data Extraction, Title-Code Mapping, and Reporting Automation",

        desc:
            "An enterprise Python and React automation platform that transforms department-level banking data into a Bangladesh Bank RIT master workbook through Title Code mapping, authoritative source routing, validation, formula protection, audit logging, review workflows, and automated reporting preparation.",

        objective:
            "Automate the collection, validation, extraction, mapping, insertion, review, and reporting of Bangladesh Bank RIT data while eliminating repetitive manual Excel manipulation and preserving source-level traceability.",

        type: "Banking Automation Platform",

        risk: "High",

        coverage: "Enterprise Regulatory Reporting",

        status: "Operational",

        tags: [
            "Python",
            "FastAPI",
            "React",
            "Excel Automation",
            "Title Code Mapping",
            "ETL",
            "SQLite",
            "Audit Logging"
        ],

        accent: "green",

        icon: FileSpreadsheet,

        images: [
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-1.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-2.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-3.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-4.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-5.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-6.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-7.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-8.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-9.png",
            "/assets/images/Projects/SBAC-RIT/SBAC-RIT-10.png",
        ],

        summary:
            "Built to convert fragmented department-level banking inputs into a controlled regulatory reporting workflow using Title Code-driven extraction, source authority zones, formula-aware master updates, audit trails, human review, and downstream CSV preparation.",

        pipeline: [

            {
                title: "Multi-source departmental ingestion",

                desc:
                    "The platform accepts structured Excel and legacy source files from IT/E-Banking, CARD, BACH, Agent Banking, Bangla Pay, and Remittance while preserving compatibility with existing CBS-generated IT reports.",
            },

            {
                title: "Reporting-period validation",

                desc:
                    "Source reporting dates are detected and validated against the previous-month master, with the current reporting period propagated into the generated master while ignoring unrelated system/current-date fields.",
            },

            {
                title: "Title Code-based extraction",

                desc:
                    "Structured departmental sources use Title Code as the canonical reporting identity, while the CBS-generated IT source uses row alignment to locate the corresponding master row and validates its Title Code and descriptors.",
            },

            {
                title: "Authoritative source routing",

                desc:
                    "Dedicated departments are assigned authoritative master zones so overlapping IT mirror data does not create unnecessary conflicts or overwrite department-owned fields.",
            },

            {
                title: "Data insertion and formula protection",

                desc:
                    "Source values are inserted into the correct Numeric Value or Textual Value fields while BB formula-driven cells remain protected and continue to derive their values from the updated inputs.",
            },

            {
                title: "Calculation and reconciliation",

                desc:
                    "Bangla Pay APP+WEB fields are calculated from the designated APP and WEB sources, while direct APP and WEB sections follow their specific authority rules.",
            },

            {
                title: "Review and audit workflow",

                desc:
                    "Ambiguous or conflicting records are routed to a review queue instead of being silently overwritten, with Insert/Discard decisions and processing events retained for auditability.",
            },

            {
                title: "Output and reporting preparation",

                desc:
                    "The processed master is generated with its updated reporting period, archived with processing metadata and logs, and prepared for downstream Bangladesh Bank CSV generation.",
            },

        ],

        sourceEngine: {

            method:
                "Title Code-first ETL processing with source-specific adapters, authoritative row zones, reporting-period validation, formula awareness, and audit logging.",

            supportedSources: [

                "CBS-generated E-Banking and E-Commerce Returns",

                "CARD departmental source",

                "BACH departmental source",

                "Agent Banking source",

                "Mobile Banking source",

                "Structured Remittance workbook",

                "Legacy Remittance text source",

            ],

            outputs: [

                "Processed regulatory reporting master workbook",

                "Processing event log",

                "Review queue",

                "Audit database records",

                "Archived processing run",

                "CSV-ready output",

            ],

        },

        authorityModel: {

            sources: [
                "Department-specific authoritative data sources",
                "CBS-generated enterprise reporting data",
                "Specialized banking service datasets",
            ],

            principle:
                "Each reporting domain has a defined authoritative source, allowing the platform to resolve overlapping data deterministically while preventing unrelated sources from overwriting controlled fields.",

        },

        banglaPay: {

            dataModel: [
                "APP reporting data",
                "WEB reporting data",
                "Combined APP+WEB reporting data",
            ],

            calculation:
                "Combined APP+WEB values are derived from the corresponding APP and WEB source data using the reporting rules defined by the platform.",

        },

        dataValidation: {

            primaryKey:
                "Title Code",

            validation:

                "Reporting period validation, Title Code resolution, IT row alignment, Item Name and Item Name Details validation, source authority checks, formula detection, numeric/textual type handling, and duplicate/conflict detection.",

            protections: [

                "BB formula cells are never overwritten",

                "Failed source lookups do not blindly erase existing values",

                "Multiple competing source candidates are routed to review",

                "N/A values requiring business interpretation remain review-controlled",

                "Dedicated source zones prevent non-owning sources from competing",

            ],

        },

        audit: [

            "Timestamped processing batches",

            "Source file SHA-256 tracking",

            "Source-read events",

            "Insert and update events",

            "Calculation events",

            "Review events",

            "Formula skip events",

            "Error logging",

            "SQLite audit records",

            "Archived processing runs",

        ],

        reviewWorkflow: [

            "Detect source conflict or unresolved business rule",

            "Create auditable review item",

            "Display source value, target row, Title Code, and rule",

            "Allow controlled Insert or Discard decision",

            "Preserve decision history",

        ],

        visualization: [

            "Processing dashboard",

            "Inserted / updated / unchanged statistics",

            "Formula and review metrics",

            "Source activity visualization",

            "Processing history",

            "Event and log explorer",

            "Review center",

            "Calculation inspection",

            "Output and archive management",

        ],

        auditUse: [

            "Regulatory reporting preparation",

            "Department-level source traceability",

            "Processing audit evidence",

            "Exception and review management",

            "Historical run archiving",

            "CSV preparation for Bangladesh Bank submission",

        ],

        highlights: [

            "Title Code-driven enterprise regulatory reporting automation",

            "Supports both structured departmental templates and legacy CBS-generated IT files",

            "Authoritative source zones eliminate unnecessary cross-department conflicts",

            "Formula-aware master processing preserves Bangladesh Bank calculations",

            "APP+WEB calculation engine for Bangla Pay reporting",

            "Human-in-the-loop review for genuine conflicts and exceptions",

            "Timestamped processing logs and SQLite audit records",

            "Department-specific standardized source templates",

            "Previous-month master → current-month source workflow",

        ],

        stack: [

            "Python",

            "FastAPI",

            "React",

            "openpyxl",

            "SQLite",

            "Excel/XLSM Processing",

            "ETL / Data Mapping",

            "CSV Generation",

            "Audit Logging",

        ],

        impact: [

            "Reduced repetitive manual RIT master-data insertion work",

            "Standardized department-to-master data flow using Title Codes",

            "Improved traceability of regulatory reporting data",

            "Reduced accidental overwrites through source authority and formula protection",

            "Created a repeatable monthly reporting workflow",

            "Enabled centralized review, audit, and archive management",

        ],

        relatedProjects: [

            "Department-specific source templates were designed to standardize recurring monthly inputs.",

            "The platform is being extended toward a GUI-managed source lifecycle so users no longer need to manually move, rename, or manage backend files.",

            "Future source-agent architecture will allow departments to provide standardized data directly into the automation pipeline.",

        ],

        customComponent: "SBACRITAutomation",

    },
 
    {
        title: "Attendance Sync Reliability Pipeline",
        subtitle: "Attendance Scheduler and Consistency Engine",
        desc: "A ZKTeco attendance collection pipeline with scheduled polling, duplicate prevention, and reliable synchronization into the HR application.",
        objective:
            "Collect attendance data reliably from distributed ZKTeco devices, prevent duplicates, and forward clean records into the HR platform with better operational consistency.",
        type: "Automation Pipeline",
        risk: "Medium",
        coverage: "Attendance Systems",
        status: "Operational",
        tags: ["Flask", "Python", "ThreadPool", "MySQL"],
        accent: "violet",
        icon: BarChart3,
        images: [
            "/assets/images/Projects/Att-System/Att-1.png",
            "/assets/images/Projects/Att-System/Att-2.png",
            "/assets/images/Projects/Att-System/Att-3.png",
            "/assets/images/Projects/Att-System/Att-4.png",
            "/assets/images/Projects/Att-System/Att-5.png",
            "/assets/images/Projects/Att-System/Att-6.png",
            "/assets/images/Projects/Att-System/Att-7.png",
            "/assets/images/Projects/Att-System/Att-8.png",
        ],
        summary:
            "Designed for reliable attendance collection, consistency control, and clean forwarding of validated records to the HR platform.",
        pipeline: [
            {
                title: "Scheduled polling",
                desc: "Collects data from ZKTeco devices on a schedule so attendance records are gathered continuously and predictably.",
            },
            {
                title: "Parallel device scanning",
                desc: "Uses thread-pool based execution to query multiple devices efficiently across branches and reduce collection time.",
            },
            {
                title: "Consistency control",
                desc: "Applies atomic locks and duplicate checks to prevent repeated entries and protect record integrity.",
            },
            {
                title: "Local staging",
                desc: "Stores validated data locally before sync so records remain available even if the downstream HR system is temporarily unavailable.",
            },
            {
            title: "HR synchronization",
            desc: "Forwards clean, validated records into the HR application after checks are complete.",
            },
        ],
        highlights: [
            "Scheduled polling from ZKTeco devices",
            "Thread pool based parallel scanning for speed and scale",
            "Atomic locks and duplicate checks for consistency",
            "Stored local copies before HR synchronization",
            "Frontend tracks uptime, reachability, and previous data states",
        ],
        visualization: [
            "Device uptime view",
            "Branch reachability status",
            "Historical sync state tracking",
            "Operational monitoring dashboard",
        ],
        auditUse: [
            "Helps validate attendance sync reliability during audits",
            "Supports branch operational checks and troubleshooting",
            "Provides evidence of consistent data handling",
        ],
        stack: ["Python", "Flask", "MySQL", "ThreadPoolExecutor", "Atomic Locks"],
        impact: [
            "Reduced data inconsistency during sync",
            "Improved reliability of attendance collection",
            "Increased operational visibility across branches",
        ],
        relatedProjects: [
            "Part of the broader automation ecosystem used for branch operations and data reliability.",
        ],
    },

    {
        title: "DVR Fleet Administration",
        subtitle: "Time Sync, Bulk Control, and Configuration Automation",
        desc: "A full-stack DVR fleet management system combining React-based control dashboard, Python orchestration backend, and PowerShell execution layer for managing mixed Hikvision firmware environments.",

        objective:
            "Centralize DVR fleet operations so time drift, password management, and configuration updates can be handled consistently across distributed banking branches with heterogeneous firmware behavior.",

        type: "Device Management",
        risk: "Medium",
        coverage: "CCTV / DVR Fleet",
        status: "Operational",

        tags: [
            "React Frontend",
            "Python Backend",
            "PowerShell",
            "Hikvision API",
            "NTP",
            "Automation",
            "JSON Orchestration"
        ],

        accent: "green",
        icon: Video,

        images: [
            "/assets/images/Projects/DVR-System/DVR-1.png",
            "/assets/images/Projects/DVR-System/DVR-2.png",
        ],

        summary:
            "Built a full-stack DVR control platform featuring a React-based operational dashboard, Python orchestration backend, and PowerShell execution engine. The system handles mixed Hikvision firmware inconsistencies through adaptive authentication logic, fallback admin identity resolution, and structured JSON-based device reporting.",

        pipeline: [
            {
                title: "React control dashboard",
                desc: "Frontend interface for monitoring DVR fleet health, executing bulk operations, and visualizing device status in real time.",
            },
            {
                title: "Device time validation",
                desc: "Checks DVR clock accuracy and detects drift affecting logs and forensic traceability.",
            },
            {
                title: "Firmware-aware NTP enforcement",
                desc: "Applies time synchronization logic adapted to different Hikvision firmware behaviors.",
            },
            {
                title: "Bulk password automation engine",
                desc: "Executes PowerShell-based password updates with fallback admin identity handling across heterogeneous devices.",
            },
            {
                title: "Python orchestration layer",
                desc: "Coordinates execution, handles retries, aggregates responses, and returns structured JSON status per device.",
            },
            {
                title: "System health monitoring",
                desc: "Collects DVR memory usage and storage availability metrics for operational visibility.",
            },
        ],

        highlights: [
            "Full-stack DVR management system (React + Python + PowerShell)",
            "Mixed firmware handling with adaptive authentication logic",
            "Fallback admin identity resolution for Hikvision device variations",
            "Structured JSON-based execution and reporting pipeline",
            "Device storage and memory monitoring integrated into control dashboard",
        ],

        visualization: [
            "Fleet health dashboard (React UI)",
            "Device status & uptime panels",
            "Time sync drift monitoring",
            "Configuration execution logs",
        ],

        auditUse: [
            "Supports CCTV operational audit checks across branches",
            "Provides centralized visibility for device configuration control",
            "Documents controlled administrative actions for compliance review",
        ],

        stack: [
            "React",
            "Python Backend",
            "PowerShell",
            "Hikvision API",
            "NTP",
            "Automation",
        ],

        impact: [
            "Reduced manual DVR administration across branches",
            "Improved reliability across mixed firmware Hikvision deployments",
            "Enabled scalable bulk device management through a unified control plane",
        ],

        relatedProjects: [
            "Supports enterprise branch infrastructure automation and security operations workflows.",
        ],
    },

    {
        title: "SafeOps Endpoint Health Review",
        subtitle: "PC Health and Status Checks",
        desc: "An operational utility for checking endpoint health, configuration state, and readiness across distributed branches.",
        objective:
            "Provide a quick way to inspect branch endpoint health, surface readiness issues, and guide remediation during support or audit activities.",
        type: "Health Monitoring",
        risk: "Low",
        coverage: "Branch Endpoints",
        status: "Operational",
        tags: ["Python", "Health Checks", "Operations", "Support"],
        accent: "blue",
        icon: Code2,
        images: [
            "/assets/images/Projects/SafeOps/Safe-1.png",
            "/assets/images/Projects/SafeOps/Safe-2.png",
            "/assets/images/Projects/SafeOps/Safe-3.png",
            "/assets/images/Projects/SafeOps/Safe-4.png",
            "/assets/images/Projects/SafeOps/Safe-5.png",
        ],
        summary:
            "Provides branch-wise scanning, health status display, remediation guidance, and SOP-aligned validation for endpoints.",
        pipeline: [
            {
                title: "Branch-wise scanning",
                desc: "Checks endpoint status across distributed branches so operational teams can quickly identify unhealthy systems.",
            },
            {
                title: "Health validation",
                desc: "Verifies readiness signals and highlights systems that need attention or remediation.",
            },
            {
                title: "Remediation guidance",
                desc: "Surfaces SOP-aligned next steps so support teams can act consistently.",
            },
            {
                title: "Operational review",
                desc: "Lets teams use the results for readiness checks, support validation, and audit preparation.",
            },
        ],
        highlights: [
            "Branch-wise scanning for distributed infrastructure visibility",
            "Real-time endpoint health status display",
            "Remediation guidance aligned with SOP",
            "Useful for audits, readiness checks, and support validation",
        ],
        visualization: [
            "Branch health cards",
            "Status indicators",
            "Readiness progress views",
            "Operational validation summary",
        ],
        auditUse: [
            "Supports endpoint health evidence for branch audits",
            "Helps document readiness and remediation status",
            "Useful for periodic operational validation",
        ],
        stack: ["Python", "Monitoring", "Health Checks", "Operational Support"],
        impact: [
            "Improved endpoint readiness checks",
            "Helped operational support teams validate status quickly",
            "Added visibility for audit and remediation workflows",
        ],
        relatedProjects: [
            "Complements the inventory and migration workflow by validating endpoint readiness after changes.",
        ],
    },

    {
        title: "Active Directory Migration & Automation",
        subtitle: "Domain Migration Framework",
        desc: "A large-scale Active Directory migration framework with automated post-deployment setup and branch-wide workstation handling.",
        objective:
            "Migrate branch endpoints from workgroup to domain while automating post-migration setup, reducing manual effort, and standardizing workstation configuration at scale.",
        type: "Migration",
        risk: "High",
        coverage: "125+ Branches",
        status: "Operational",
        tags: ["PowerShell", "AD", "Automation", "Enterprise"],
        accent: "indigo",
        icon: Server,
        images: [
            "/assets/images/Case Studies/AD/AD-1.png",
            "/assets/images/Case Studies/AD/AD-2.png",
            "/assets/images/Case Studies/AD/AD-3.png",
            "/assets/images/Case Studies/AD/AD-4.png",
            "/assets/images/Case Studies/AD/AD-5.png",
        ],
        summary:
            "Migrated workgroup systems into domain-managed environments and automated post-installation setup across 125+ branches.",
        pipeline: [
            {
                title: "Pre-migration preparation",
                desc: "Prepared endpoints for migration by aligning configuration and identifying workgroup systems that needed domain onboarding.",
            },
            {
                title: "Domain migration",
                desc: "Moved endpoints from workgroup to domain-managed identity across a large branch footprint.",
            },
            {
                title: "Post-domain automation",
                desc: "Applied reusable PowerShell scripts for software installation, policy enforcement, and system setup after migration.",
            },
            {
                title: "User environment restoration",
                desc: "Restored desktops, documents, and shortcuts so users could resume work with minimal disruption.",
            },
        ],
        highlights: [
            "Migrated endpoints from workgroup to domain across 125+ branches",
            "Reusable PowerShell framework for post-domain configuration",
            "Automated software installation, licensing, and policy enforcement",
            "Restored user desktops, documents, and shortcuts after migration",
        ],
        visualization: [
            "Migration progress tracking",
            "Branch rollout status",
            "Post-domain setup completion",
            "Configuration consistency view",
        ],
        auditUse: [
            "Useful for documenting domain migration progress",
            "Supports branch rollout evidence and post-change validation",
            "Helps demonstrate standardized endpoint management",
        ],
        stack: ["PowerShell", "Active Directory", "GPO", "Deployment Automation"],
        impact: [
            "Standardized workstation configuration at scale",
            "Reduced migration downtime and manual setup effort",
            "Improved consistency across branches",
        ],
        relatedProjects: [
            "This migration work is closely connected to the PC Inventory & Risk Analysis Platform, which helped identify and validate migration candidates.",
        ],
    },

    {
        title: "Legacy System Upgrade & Hardware Optimization",
        subtitle: "System Modernization Strategy",
        desc: "A remediation strategy for legacy Windows 7 environments using hardware upgrades where possible and Linux fallback options where upgrades were not feasible.",
        objective:
            "Reduce unsupported OS exposure by identifying legacy systems, upgrading hardware where possible, and applying secure fallback options where constraints existed.",
        type: "Modernization Program",
        risk: "High",
        coverage: "Legacy Branch Systems",
        status: "Operational",
        tags: ["Windows", "Linux", "Hardware", "Operations"],
        accent: "orange",
        icon: Box,
        images: ["/assets/images/legacy-upgrade.png"],
        summary:
            "Improved security posture by identifying outdated systems, replacing unsupported hardware where possible, and using Linux alternatives where needed.",
        pipeline: [
            {
                title: "Legacy system identification",
                desc: "Detected Windows 7 and other outdated systems across branches that needed remediation.",
            },
            {
                title: "Hardware evaluation",
                desc: "Assessed whether devices could be upgraded to run supported operating systems reliably.",
            },
            {
                title: "Fallback modernization",
                desc: "Used Linux alternatives for constrained systems where full upgrades were not practical.",
            },
            {
                title: "Security posture improvement",
                desc: "Removed unsupported OS exposure and improved the reliability of branch endpoints.",
            },
        ],
        highlights: [
            "Identified and audited legacy Windows 7 systems across branches",
            "Performed hardware upgrades to support modern operating systems",
            "Deployed Linux alternatives for constrained devices",
            "Improved security by removing unsupported OS usage",
        ],
        visualization: [
            "Legacy system reduction charts",
            "Upgrade readiness view",
            "Hardware replacement tracking",
            "Branch remediation progress",
        ],
        auditUse: [
            "Provides evidence for legacy OS remediation",
            "Useful for compliance and audit reporting",
            "Supports modernization planning and control tracking",
        ],
        stack: ["Windows 7", "Linux", "Hardware Refresh", "Asset Remediation"],
        impact: [
            "Reduced legacy OS exposure",
            "Extended usable hardware lifecycle",
            "Balanced security, cost, and operational continuity",
        ],
        relatedProjects: [
            "This modernization work used the inventory and risk platform to identify outdated systems and validate remediation progress.",
        ],
    },
];